import { Layers3, Route } from "lucide-react";
import {
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CalculatorOutlined,
  SaveOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
  DollarOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Layout,
  Space,
  Tabs,
  Menu,
  Modal,
  Tag,
  Alert,
} from "antd";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "../../contexts/DevModeContext";
import { useChangeDetectionContext } from "../../contexts/ChangeDetectionContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useAuth } from "../../hooks/useAuth";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { useGroundworksTotalCalculations } from "../../hooks/useGroundworksTotalCalculations";
import { useRoadsAndSewersTotalSync } from "../../hooks/useRoadsAndSewersTotalSync";
import { useInactivityTimer } from "../../hooks/useInactivityTimer";
import { COLORS } from "../../utils/constants";
import { saveGeoToberWallBuildUp } from "../../utils/wallsBuildUpSave";
import Header from "../common/Header";
import SessionIdleModal from "../common/SessionIdleModal";
import UnsavedChangesModal from "../common/UnsavedChangesModal";
import GlobalSettingsCard from "../common/GlobalSettingsCard";
import {
  forceSaveBothBoqEndpoints,
} from "../../utils/boqVersionUtils";
import { useBoqLatestVersion, useBoqSelectedVersion, useBoqVersionHistory } from "../../hooks/useBillOfQuantities";
import { getScopedCurrentVersionNumber, getBoqVersionPrefixFromValue, getNumericBoqVersion } from "../../utils/boqVersionPrefix";
import MJ_LOGO from "@/assets/mnj-logo.jpg";

const { Content, Footer, Sider } = Layout;

// Sidebar menu labels. Display only — routing/tab keys ("groundworks" / "roadsAndSewers") are unchanged.
const GROUNDWORKS_MENU_LABEL = "Groundworks Take - Off";
const ROADS_SEWERS_MENU_LABEL = "Roads & Sewers Take - Off";

interface MainLayoutProps {
  children: React.ReactNode;
  showFooter?: boolean;
  canProceed?: boolean;
  onSave?: () => void;
  onNext?: () => void;
  onSaveAndExit?: () => void;
  showStepper?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  showFooter = true,
  canProceed = true,
  onSave,
  onNext,
  onSaveAndExit,
  showStepper = true,
}) => {
  const { saveTender, state, updateField, setActiveJobType, setViewMode } = useTender();
  const isDevLocalhost = useDevMode();
  const location = useLocation();
  const isViewMode = state.isViewMode || false;
  const { data: itemLibraryData, isLoading: isItemLibraryLoading } = useItemLibrary(state.region || "");

  // Keeps tender.roadsAndSewersTotals in sync regardless of which page is active —
  // RsSumAnalysisBillSummary.tsx (Review & Calculate's Sum Analysis tab) only computes
  // this while mounted, so without a stable, always-mounted sync point here, saving
  // from any other R&S page would persist whatever total was last computed there.
  useRoadsAndSewersTotalSync(itemLibraryData, isItemLibraryLoading);

  // Read include-flag checkbox states from localStorage — same keys as Sum Analysis and StepperNavigation
  const _gwPrelimPct = (state.globalSettings as unknown as Record<string, Record<string, number>>)?.groundworks?.preliminaries || 0;
  const _gwOhpPct = (state.globalSettings as unknown as Record<string, Record<string, number>>)?.groundworks?.overheadAndProfit || 0;
  const _gwDiscountPct = (state.globalSettings as unknown as Record<string, Record<string, number>>)?.groundworks?.discount || 0;
  const _getCheckbox = (key: string, fallback: number) => { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) as boolean : fallback > 0; };
  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries: _getCheckbox("groundworks_includePreliminariesDisplay", _gwPrelimPct),
    includeOhp: _getCheckbox("groundworks_includeOhpDisplay", _gwOhpPct),
    includeDiscount: _getCheckbox("groundworks_includeDiscountDisplay", _gwDiscountPct),
  });

  // Always-current refs so callbacks defined in earlier renders still read
  // the latest state/totals (avoids stale-closure bugs in async handlers).
  const latestStateRef = useRef(state);
  latestStateRef.current = state;
  const latestGroundworksTotalsRef = useRef(groundworksTotals);
  latestGroundworksTotalsRef.current = groundworksTotals;

  const isHistoric = localStorage.getItem("isHistoricalVersion") === "true";
  // The /tender/* creation flow (job-types/import-opportunities/import-takeoff)
  // renders no stepper/sidebar/tabs, so boqApiJobTypes/boqApiTotals below are
  // never read there - skip fetching /boq-versions until a tender actually
  // exists and the user has moved into the real workflow.
  const isTenderCreationRoute = location.pathname.startsWith("/tender/");
  // toBoqVersionKey/getBoqVersionRoutePrefix only recognize /groundworks/* and
  // /roads-sewers/* — every other page MainLayout renders on (dashboard,
  // /version-history, /audit-trail, /document-library, /bill-quantities,
  // /rate-engine, etc.) would otherwise resolve the "latest"/"selected"
  // version as unscoped, sending a bare numeric version to the boq-versions
  // GET instead of GWKS-N/RS-N. Fall back to state.activeJobType there.
  // Left undefined on the two workflow routes themselves so their existing
  // URL-based detection is unaffected — a combined-job-type tender's
  // activeJobType may not match whichever of the two workflows is on screen.
  const isWorkflowRoute = location.pathname.startsWith("/groundworks/") || location.pathname.startsWith("/roads-sewers/");
  const boqVersionPrefixOverride = isWorkflowRoute
    ? undefined
    : state.activeJobType === "groundworks"
      ? "GWKS" as const
      : state.activeJobType === "roadsAndSewers"
        ? "RS" as const
        : null;
  const { data: latestVersionData } = useBoqLatestVersion(state.projectName || undefined, { enabled: !isHistoric && !isTenderCreationRoute, oppNo: state.opportunityNo, prefixOverride: boqVersionPrefixOverride });
  const { data: selectedVersionData } = useBoqSelectedVersion(state.projectName || undefined, { enabled: isHistoric && !isTenderCreationRoute, oppNo: state.opportunityNo, prefixOverride: boqVersionPrefixOverride });
  const versionData = isHistoric ? selectedVersionData : latestVersionData;

  // Sidebar tab version labels — Groundworks and Roads & Sewers keep
  // independent GWKS-N/RS-N sequences, so each tab's "(vN)" must come from
  // its own scoped current version, not one shared localStorage value.
  const { data: sidebarVersionHistory } = useBoqVersionHistory(state.projectName || undefined, { enabled: !isTenderCreationRoute, oppNo: state.opportunityNo });

  // While viewing a historical version (isHistoric), the tab whose GWKS-/RS-
  // sequence matches the loaded snapshot must show the exact version being
  // viewed, not that sequence's current/latest number — otherwise reopening
  // an old GWKS-3 review still shows "Groundworks (v5)" if v5 has since been
  // created. The other job type (not the one the user clicked into history
  // for) has no specific version loaded here, so it keeps showing its own
  // current/latest number same as in live/edit mode.
  const historicVersionPrefix = isHistoric ? getBoqVersionPrefixFromValue(versionData?.version) : null;
  const historicVersionNumber = isHistoric && versionData?.version !== undefined
    ? getNumericBoqVersion(versionData.version)
    : undefined;

  const groundworksCurrentVersion = useMemo(() => {
    if (historicVersionPrefix === "GWKS") return historicVersionNumber;
    return sidebarVersionHistory ? getScopedCurrentVersionNumber(sidebarVersionHistory, "GWKS") : undefined;
  }, [sidebarVersionHistory, historicVersionPrefix, historicVersionNumber]);
  const roadsAndSewersCurrentVersion = useMemo(() => {
    if (historicVersionPrefix === "RS") return historicVersionNumber;
    return sidebarVersionHistory ? getScopedCurrentVersionNumber(sidebarVersionHistory, "RS") : undefined;
  }, [sidebarVersionHistory, historicVersionPrefix, historicVersionNumber]);

  // Read job-types and section totals from the boq-versions payload.
  const boqApiJobTypes = useMemo(() => {
    const jt = (versionData?.boq as Record<string, unknown> | undefined)?.['job-types'] as
      | { groundworks?: boolean; roadsAndSewers?: boolean }
      | undefined;
    if (jt && (jt.groundworks || jt.roadsAndSewers)) {
      return { roadsAndSewers: jt.roadsAndSewers === true, groundworks: jt.groundworks === true };
    }
    return null;
  }, [versionData]);

  const boqApiTotals = useMemo(() => {
    const boq = versionData?.boq as Record<string, unknown> | undefined;
    const gw = (boq?.groundworks as Record<string, unknown> | undefined)?.totals as Record<string, number> | undefined;
    const rs = (boq?.roadAndSewers as Record<string, unknown> | undefined)?.totals as Record<string, number> | undefined;
    return {
      groundworks: gw?.grandTotal ?? 0,
      roadsAndSewers: rs?.grandTotal ?? 0,
    };
  }, [versionData]);

  const navigate = useNavigate();
  const { user } = useAuth();

  // Groundworks/Roads & Sewers workflow pages have nothing to render without
  // an open tender (no BOQ, no totals, no job-type context) - hitting one of
  // these URLs directly (e.g. pasting /groundworks/cut-fills with no tender
  // selected) previously rendered the page against empty TenderContext state.
  // Send the user back to the Tenders Dashboard to pick a tender first.
  useEffect(() => {
    if (isWorkflowRoute && !state.projectName) {
      navigate("/", { replace: true });
    }
  }, [isWorkflowRoute, state.projectName, navigate]);

  const { hasAnyChanges } = useChangeDetectionContext();
  const hasUnsavedChanges = state.isDirty || hasAnyChanges;

  // Prevent accidental tab close / browser refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Session idle modal state
  const [isIdleModalOpen, setIsIdleModalOpen] = useState(false);
  const [isIdleSaving, setIsIdleSaving] = useState(false);

  // Only show the idle prompt when there are unsaved changes and the user is
  // on a groundworks or roads & sewers workflow page (not view-only).
  const isGroundworksPage = location.pathname.startsWith("/groundworks/");
  const isRoadsSewersPage = location.pathname.startsWith("/roads-sewers/");
  const idleTimerEnabled =
    (isGroundworksPage || isRoadsSewersPage) && hasUnsavedChanges && !isViewMode && !isIdleModalOpen;

  const handleIdleTriggered = useCallback(() => {
    setIsIdleModalOpen(true);
  }, []);

  const { reset: resetIdleTimer } = useInactivityTimer({
    onIdle: handleIdleTriggered,
    enabled: idleTimerEnabled,
  });

  const handleIdleCancel = useCallback(() => {
    setIsIdleModalOpen(false);
    resetIdleTimer();
  }, [resetIdleTimer]);

  const handleIdleSave = useCallback(async () => {
    setIsIdleSaving(true);
    try {
      await saveTender();
      // Roads & Sewers totals are kept continuously in sync on tender state by
      // useRoadsAndSewersTotalSync (mounted above, regardless of active page),
      // so passing `undefined` here lets forceSaveBothBoqEndpoints compute its
      // payload from that already-fresh state — same as handleNavBlockSave does.
      const isGroundworksRoute = location.pathname.startsWith("/groundworks/");
      await forceSaveBothBoqEndpoints(
        state,
        "Idle session save",
        isGroundworksRoute
          ? {
              grandTotal: groundworksTotals.grandTotal,
              grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
              preliminariesAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.preliminariesAmount, 0),
              ohpAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.ohpAmount, 0),
              discountAmount: groundworksTotals.discountAmount,
              tenderTotal: groundworksTotals.tenderTotal,
              preliminariesPercent: groundworksTotals.preliminariesPercent,
              ohpPercent: groundworksTotals.ohpPercent,
              discountPercent: groundworksTotals.discountPercent,
            }
          : undefined,
        user?.name,
        itemLibraryData,
        state.opportunityNo,
      );
    } catch (error) {
      console.error("Error saving from idle modal:", error);
    } finally {
      setIsIdleSaving(false);
      setIsIdleModalOpen(false);
      resetIdleTimer();
    }
  }, [
    saveTender,
    state,
    user?.name,
    itemLibraryData,
    state.opportunityNo,
    resetIdleTimer,
    groundworksTotals,
    location.pathname,
  ]);

  // Unsaved changes navigation guard
  // When on a groundworks OR roads & sewers data-entry route with unsaved
  // changes, intercept navigation to a destination outside that same workflow
  // and show a confirmation modal.
  const GROUNDWORKS_DATA_ROUTES = [
    "/groundworks/cut-fills",
    "/groundworks/foundations",
    "/groundworks/drainage",
    "/groundworks/plot-drives",
    "/groundworks/plot-service",
    "/groundworks/plot-pavings",
    "/groundworks/scaffold",
    "/groundworks/garden",
    "/groundworks/retaining-walls",
    "/groundworks/geo-tober-wall",
  ];

  // Mirrors GROUNDWORKS_DATA_ROUTES above — every Roads & Sewers data-entry
  // page except /roads-sewers/review, same as /groundworks/review is excluded
  // from GROUNDWORKS_DATA_ROUTES.
  const ROADS_SEWERS_DATA_ROUTES = [
    "/roads-sewers/preliminary",
    "/roads-sewers/foul-sewers",
    "/roads-sewers/storm-sewers",
    "/roads-sewers/roads",
    "/roads-sewers/main-services-substation",
    "/roads-sewers/pos",
    "/roads-sewers/spoil-reconciliation",
    "/roads-sewers/compound",
  ];

  const isOnGroundworksDataRoute = GROUNDWORKS_DATA_ROUTES.includes(
    location.pathname,
  );
  const isOnRoadsSewersDataRoute = ROADS_SEWERS_DATA_ROUTES.includes(
    location.pathname,
  );

  const [navBlockModalOpen, setNavBlockModalOpen] = useState(false);
  const [navBlockPendingPath, setNavBlockPendingPath] = useState<string | null>(
    null,
  );
  const [isNavBlockSaving, setIsNavBlockSaving] = useState(false);

  // Returns true if navigation should be intercepted; shows the modal and returns true.
  // Returns false if navigation can proceed immediately.
  const guardNavigation = (destination: string): boolean => {
    const isGoingToGroundworks = destination.startsWith("/groundworks/");
    const isGoingToRoadsAndSewers = destination.startsWith("/roads-sewers/");
    const isGoingToDocumentLibrary = destination === "/document-library";
    // Only block leaving the CURRENT workflow — navigating between pages
    // within the same one (e.g. Foul Sewers -> Storm Sewers) never blocks.
    const isLeavingCurrentWorkflow = isOnGroundworksDataRoute
      ? !isGoingToGroundworks
      : !isGoingToRoadsAndSewers;
    if (
      (isOnGroundworksDataRoute || isOnRoadsSewersDataRoute) &&
      isLeavingCurrentWorkflow &&
      !isGoingToDocumentLibrary &&
      hasUnsavedChanges &&
      !isViewMode
    ) {
      setNavBlockPendingPath(destination);
      setNavBlockModalOpen(true);
      return true;
    }
    return false;
  };

  const handleNavBlockCancel = useCallback(() => {
    setNavBlockModalOpen(false);
    setNavBlockPendingPath(null);
  }, []);

  // Discard the in-progress edits and navigate to the originally-requested
  // path without saving. Wired to the "Continue without Saving" button on
  // the UnsavedChangesModal — distinct from the `×` close icon which keeps
  // the user on the current page.
  const handleNavBlockDiscardAndContinue = useCallback(() => {
    const destination = navBlockPendingPath;
    setNavBlockModalOpen(false);
    setNavBlockPendingPath(null);
    if (destination) {
      navigate(destination);
    }
  }, [navBlockPendingPath, navigate]);

  const handleNavBlockSave = useCallback(async () => {
    if (!navBlockPendingPath) return;
    setIsNavBlockSaving(true);
    try {
      const isGroundworksRoute = location.pathname.startsWith("/groundworks/");
      const prelimsAmount = groundworksTotals.sections.reduce((sum, s) => sum + s.preliminariesAmount, 0);
      const ohpAmount = groundworksTotals.sections.reduce((sum, s) => sum + s.ohpAmount, 0);

      if (isGroundworksRoute) {
        // Mirror StepperNavigation's handleCalculateReview — write hook-calculated
        // totals into state before saving so the BOQ payload is always consistent
        updateField("groundworks", {
          ...state.groundworks,
          adjustments: {
            preliminaries: prelimsAmount,
            ohp: ohpAmount,
            discount: groundworksTotals.discountAmount,
            includePreliminaries: groundworksTotals.preliminariesPercent > 0,
            includeOhp: groundworksTotals.ohpPercent > 0,
            includeDiscount: groundworksTotals.discountPercent > 0,
          },
          totals: {
            ...state.groundworks?.totals,
            grandTotal: groundworksTotals.grandTotal,
            totalWithAdjustments: groundworksTotals.tenderTotal,
            tenderTotal: groundworksTotals.tenderTotal,
          },
        });

        updateField("globalSettings", {
          ...state.globalSettings,
          groundworks: {
            overheadAndProfit: groundworksTotals.ohpPercent,
            preliminaries: groundworksTotals.preliminariesPercent,
            discount: groundworksTotals.discountPercent,
          },
        });
      }

      await saveTender();
      // Allow React state updates to propagate before reading state in forceSave
      await new Promise((resolve) => setTimeout(resolve, 200));

      const totalsToPass = isGroundworksRoute
        ? {
            grandTotal: groundworksTotals.grandTotal,
            grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
            preliminariesAmount: prelimsAmount,
            ohpAmount: ohpAmount,
            discountAmount: groundworksTotals.discountAmount,
            tenderTotal: groundworksTotals.tenderTotal,
            preliminariesPercent: groundworksTotals.preliminariesPercent,
            ohpPercent: groundworksTotals.ohpPercent,
            discountPercent: groundworksTotals.discountPercent,
          }
        : undefined;

      await forceSaveBothBoqEndpoints(
        state,
        "Navigation save",
        totalsToPass,
        user?.name,
        itemLibraryData,
        state.opportunityNo,
      );
      const destination = navBlockPendingPath;
      setNavBlockModalOpen(false);
      setNavBlockPendingPath(null);
      navigate(destination);
    } catch (error) {
      console.error("Error saving before navigation:", error);
    } finally {
      setIsNavBlockSaving(false);
    }
  }, [
    navBlockPendingPath,
    saveTender,
    state,
    updateField,
    user?.name,
    itemLibraryData,
    state.opportunityNo,
    navigate,
    groundworksTotals,
    location.pathname,
  ]);

  // Derive BOQ status from cached version data
  const apiBoqStatus = useMemo(() => {
    if (!versionData) return null;
    const d = versionData as any;
    return d?.boq?.boqStatus || d?.boq?.status || d?.snapshot?.status || d?.boqStatus || d?.status || null;
  }, [versionData]);

  const currentBoqStatus = apiBoqStatus || state.boqStatus || "In-progress";
  const isBoqInProgress =
    currentBoqStatus.trim().toLowerCase().replace(/[\s_]+/g, "-") ===
    "in-progress";

  // Whether the tender itself opens in edit mode. Role-independent: an
  // In-progress BOQ is editable by every role; any other status is view-only.
  // This only drives the app-wide view mode below.
  const canEditTender = isDevLocalhost || isBoqInProgress;

  // Global Settings access is role-independent:
  //  - view: always allowed, in both view and edit mode, at any BOQ status
  //  - edit: only in edit mode and only while the BOQ is In-progress
  const canEditGlobalSettings = !isViewMode && isBoqInProgress;

  // Set view mode based on canEditTender - view mode enabled when user cannot edit
  useEffect(() => {
    if (isTenderCreationRoute) {
      // The /tender/* creation flow (job-types/import-opportunities/import-takeoff)
      // has no BOQ/version data to be "viewing" - it must always be editable. Without
      // this, a stale sessionStorage `isViewMode` flag left over from previously
      // viewing a historical tender elsewhere survives a hard reload here (it seeds
      // TenderContext's initial state, see loadInitialState in TenderContext.tsx) and
      // is never corrected below, since this route's version-data queries are
      // deliberately disabled (isTenderCreationRoute) - so apiBoqStatus stays null and
      // the isDevLocalhost/apiBoqStatus branches below never run. The CSS class this
      // drives (.view-mode-container, index.css) hides every antd Button, including
      // this page's own Continue button - so a stuck isViewMode reads as "Continue
      // disappeared" on reload rather than merely being disabled.
      setViewMode(false);
      return;
    }
    if (isDevLocalhost) {
      // On dev/localhost always stay in edit mode regardless of role or BOQ status
      const isHistoricalVersion =
        localStorage.getItem("isHistoricalVersion") === "true";
      if (!isHistoricalVersion) {
        setViewMode(false);
      }
      return;
    }
    if (apiBoqStatus) {
      // If user explicitly opened this BOQ in view-only mode (e.g. via "View" from Dashboard),
      // never override that with edit mode even if the role/status would normally allow editing.
      const isHistoricalVersion =
        localStorage.getItem("isHistoricalVersion") === "true";
      if (isHistoricalVersion) {
        setViewMode(true);
      } else {
        setViewMode(!canEditTender);
      }
    }
  }, [canEditTender, apiBoqStatus, isDevLocalhost, isTenderCreationRoute, setViewMode]);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [isReviewSelectionModalVisible, setIsReviewSelectionModalVisible] =
    useState(false);

  // Initialize sidebar state - check if this is a page reload or route navigation
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check if sessionStorage has been initialized this session
    const sessionInit = sessionStorage.getItem("sidebarSessionInit");

    if (!sessionInit) {
      // First load of the session - default to open and mark session as initialized
      sessionStorage.setItem("sidebarSessionInit", "true");
      return false; // Open
    } else {
      // Navigation within session - restore previous state
      const saved = sessionStorage.getItem("sidebarCollapsed");
      return saved ? JSON.parse(saved) : false;
    }
  });

  // Clear session flag on actual page reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem("sidebarSessionInit");
      sessionStorage.removeItem("sidebarCollapsed");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // External request to collapse/expand the sidebar (e.g. Rate Assistant chatbot
  // opening on the Activity Tree page wants the sidebar minimised to free up width).
  useEffect(() => {
    const handler = (e: Event) => {
      const collapsed = (e as CustomEvent<{ collapsed: boolean }>).detail?.collapsed;
      if (typeof collapsed !== "boolean") return;
      setSidebarCollapsed(collapsed);
      sessionStorage.setItem("sidebarCollapsed", JSON.stringify(collapsed));
    };
    window.addEventListener("sidebarCollapseRequest", handler);
    return () => window.removeEventListener("sidebarCollapseRequest", handler);
  }, []);

  // Store and restore active job type for document-library page
  useEffect(() => {
    const path = location.pathname;

    // Store the active job type when on workflow pages
    if (path.startsWith("/roads-sewers/")) {
      sessionStorage.setItem("lastActiveJobType", "roadsAndSewers");
    } else if (path.startsWith("/groundworks/")) {
      sessionStorage.setItem("lastActiveJobType", "groundworks");
    } else if (
      path === "/document-library" ||
      path === "/rate-engine/rate-manager" ||
      path === "/rate-engine/activity-tree"
    ) {
      // When navigating to document-library or rate-engine pages, store current activeJobType if not already stored
      const lastActiveJobType = sessionStorage.getItem("lastActiveJobType");
      if (!lastActiveJobType && state.activeJobType) {
        sessionStorage.setItem("lastActiveJobType", state.activeJobType);
      }

      // Restore the active job type when on document-library or rate-engine pages
      if (
        lastActiveJobType &&
        (lastActiveJobType === "roadsAndSewers" ||
          lastActiveJobType === "groundworks")
      ) {
        if (state.activeJobType !== lastActiveJobType) {
          setActiveJobType(
            lastActiveJobType as "roadsAndSewers" | "groundworks",
          );
        }
      }
    }
  }, [location.pathname, setActiveJobType, state.activeJobType]);

  // Helper function to toggle sidebar and save to sessionStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    sessionStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
    window.dispatchEvent(
      new CustomEvent("sidebarCollapseRequest", { detail: { collapsed: newState } }),
    );
  };

  // Define workflow steps for each job type
  const roadsAndSewersSteps = [
    "/roads-sewers/preliminary",
    "/roads-sewers/foul-sewers",
    "/roads-sewers/storm-sewers",
    "/roads-sewers/roads",
    "/roads-sewers/main-services-substation",
    "/roads-sewers/pos",
    "/roads-sewers/spoil-reconciliation",
    "/roads-sewers/compound",
    "/roads-sewers/review",
  ];

  const groundworksSteps = [
    "/groundworks/cut-fills",
    // "/groundworks/plot-measure",
    "/groundworks/foundations",
    "/groundworks/drainage",
    "/groundworks/plot-drives",
    "/groundworks/plot-service",
    "/groundworks/plot-pavings",
    "/groundworks/scaffold",
    "/groundworks/garden",
    "/groundworks/retaining-walls",
    "/groundworks/geo-tober-wall",
    "/groundworks/review",
    "/groundworks/plot-matrix",
  ];

  // Determine current workflow context
  const getCurrentWorkflowSteps = () => {
    const path = location.pathname;
    if (path.startsWith("/roads-sewers/")) {
      return roadsAndSewersSteps;
    } else if (path.startsWith("/groundworks/")) {
      return groundworksSteps;
    }
    // Legacy paths or default
    return [];
  };

  const currentSteps = getCurrentWorkflowSteps();
  const currentStepIndex = currentSteps.findIndex(
    (step) => step === location.pathname,
  );
  const isWorkflowStep = currentStepIndex >= 0;
  const isLastWorkflowStep = currentStepIndex === currentSteps.length - 1;

  // Check if we're on a review route where Save Draft should be hidden
  const isReviewRoute =
    location.pathname === "/roads-sewers/review" ||
    location.pathname === "/groundworks/review";

  // Check if we're on the final bill quantities page
  const isFinalStep = location.pathname === "/bill-quantities";

  // Determine if this is the last step overall (unused in current implementation)
  // const isLastStep = isFinalStep || (isLastWorkflowStep && !shouldShowBillQuantities());

  function shouldShowBillQuantities(): boolean {
    const { jobTypes, workflow } = state;

    // If both job types selected, both must be complete
    if (jobTypes.roadsAndSewers && jobTypes.groundworks) {
      return (
        workflow.roadsAndSewers.isComplete && workflow.groundworks.isComplete
      );
    }

    // If only one job type, that one must be complete
    if (jobTypes.roadsAndSewers) {
      return workflow.roadsAndSewers.isComplete;
    }

    if (jobTypes.groundworks) {
      return workflow.groundworks.isComplete;
    }

    return false;
  }

  const handleSave = async () => {
    if (onSave) {
      onSave();
    } else {
      // saveTender now calculates groundworks totals internally with fresh values
      await saveTender();

      // Force save to both BOQ endpoints
      try {
        await forceSaveBothBoqEndpoints(
          state,
          "Manual save",
          location.pathname.startsWith("/groundworks/") ? {
            grandTotal: groundworksTotals.grandTotal,
            grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
            preliminariesAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.preliminariesAmount, 0),
            ohpAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.ohpAmount, 0),
            discountAmount: groundworksTotals.discountAmount,
            tenderTotal: groundworksTotals.tenderTotal,
            preliminariesPercent: groundworksTotals.preliminariesPercent,
            ohpPercent: groundworksTotals.ohpPercent,
            discountPercent: groundworksTotals.discountPercent,
          } : undefined,
          user?.name,
          itemLibraryData,
          state.opportunityNo,
        );
      } catch (error) {
        console.error("Error saving to BOQ endpoints:", error);
      }
    }
  };

  const handleSaveAndExit = async () => {
    // saveTender calculates groundworks totals internally with fresh settings and checkbox states
    await saveTender();

    // Force save to both BOQ endpoints
    try {
      await forceSaveBothBoqEndpoints(
        state,
        "Save and exit",
        location.pathname.startsWith("/groundworks/") ? {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.preliminariesAmount, 0),
          ohpAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.ohpAmount, 0),
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        } : undefined,
        user?.name,
        itemLibraryData,
        state.opportunityNo,
      );
    } catch (error) {
      console.error("Error saving to BOQ endpoints:", error);
    }

    // Then call the exit callback
    if (onSaveAndExit) {
      onSaveAndExit();
    }
  };

  const handleNext = async () => {
    // saveTender calculates groundworks totals internally with fresh settings and checkbox states
    await saveTender();

    // Force save to both BOQ endpoints
    let savedVersion: number | undefined;
    try {
      const result = await forceSaveBothBoqEndpoints(
        state,
        "Save and continue",
        location.pathname.startsWith("/groundworks/") ? {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.preliminariesAmount, 0),
          ohpAmount: groundworksTotals.sections.reduce((s, sec) => s + sec.ohpAmount, 0),
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        } : undefined,
        user?.name,
        itemLibraryData,
        state.opportunityNo,
      );
      savedVersion = result.version;
    } catch (error) {
      console.error("Error saving to BOQ endpoints:", error);
    }

    // Post wall section build up data when leaving the geo-tober-wall page
    if (location.pathname === "/groundworks/geo-tober-wall") {
      const ohpPercent =
        (state.globalSettings as any)?.groundworks?.overheadAndProfit || 0;
      const prelimsPercent =
        (state.globalSettings as any)?.groundworks?.preliminaries || 0;
      saveGeoToberWallBuildUp({
        tender: state,
        itemLibraryData,
        ohpPercent,
        prelimsPercent,
        versionOverride: savedVersion,
      });
    }

    if (onNext) {
      onNext();
    } else if (location.pathname === "/tender-info") {
      // Navigate to job types selection after tender info
      navigate("/job-types");
    } else if (
      location.pathname === "/job-types" ||
      location.pathname === "/job-selection"
    ) {
      // Navigate to appropriate workflow after job type selection
      const { jobTypes } = state;

      if (jobTypes.roadsAndSewers && jobTypes.groundworks) {
        // Both selected - navigate to Roads & Sewers first (default)
        navigate("/roads-sewers/preliminary");
      } else if (jobTypes.roadsAndSewers) {
        // Only Roads & Sewers selected
        navigate("/roads-sewers/preliminary");
      } else if (jobTypes.groundworks) {
        // Only Groundworks selected
        navigate("/groundworks/cut-fills");
      }
    } else if (isWorkflowStep && !isLastWorkflowStep) {
      // Navigate to next step in current workflow
      navigate(currentSteps[currentStepIndex + 1]);
    } else if (isLastWorkflowStep && shouldShowBillQuantities()) {
      // Navigate to bill quantities if workflow is complete
      navigate("/bill-quantities");
    }
  };

  // Show sidebar for workflow steps and document-library page (only when tender is in context)
  const isDocumentLibraryPage = location.pathname === "/document-library";
  const isDocumentLibraryWithTender = isDocumentLibraryPage && state.projectName;
  const isRateManagerPage = location.pathname === "/rate-engine/rate-manager";
  const isActivityTreePage = location.pathname === "/rate-engine/activity-tree";
  const isRateEnginePage = isRateManagerPage || isActivityTreePage;

  // Remember last-visited rate-engine sub-route per logged-in user per
  // tender, so the sidebar "Site Rate Library" entry returns to whichever of
  // rate-manager/activity-tree that user last used for the current tender.
  const rateEngineLastRouteKey =
    user?.username && state.projectName
      ? `rateEngineLastRoute_${user.username}_${state.projectName}`
      : null;
  useEffect(() => {
    if (rateEngineLastRouteKey && isRateEnginePage) {
      localStorage.setItem(rateEngineLastRouteKey, location.pathname);
    }
  }, [rateEngineLastRouteKey, isRateEnginePage, location.pathname]);
  const isRateManagerWithProject = isRateManagerPage && !!state.projectName;
  const isActivityTreeWithProject = isActivityTreePage && !!state.projectName;
  const showSidebar = isWorkflowStep || isDocumentLibraryWithTender || isRateManagerWithProject || isActivityTreeWithProject;

  // Tab functionality for dual workflows.
  // A job type is visible when the live BOQ API says so OR when the context
  // state says so (covers the window between saving and the next API refresh).
  const { jobTypes } = state;
  const effectiveJobTypes = {
    // Historical view: rely solely on state.jobTypes (set by LOAD_PROJECT before
    // navigation in VersionHistory). boqApiTotals from selectedVersionData can be
    // stale while React Query refetches, which would incorrectly flash a tab that
    // belongs to the previously viewed version.
    // Active editing: show if explicitly selected OR BOQ reports a non-zero total.
    roadsAndSewers: isHistoric
      ? jobTypes.roadsAndSewers
      : (jobTypes.roadsAndSewers || boqApiTotals.roadsAndSewers > 0),
    groundworks: isHistoric
      ? jobTypes.groundworks
      : (jobTypes.groundworks || boqApiTotals.groundworks > 0),
  };
  const bothJobTypesSelected = effectiveJobTypes.roadsAndSewers && effectiveJobTypes.groundworks;
  const showTabs =
    bothJobTypesSelected && (isWorkflowStep || isDocumentLibraryWithTender || isRateManagerWithProject || isActivityTreeWithProject);

  const getCurrentTabKey = () => {
    const path = location.pathname;
    if (path.startsWith("/roads-sewers/")) {
      return "roadsAndSewers";
    } else if (path.startsWith("/groundworks/")) {
      return "groundworks";
    }
    // On document-library and rate-engine pages, check sessionStorage first to maintain job type across reloads
    if (path === "/document-library" || path === "/rate-engine/rate-manager" || path === "/rate-engine/activity-tree") {
      const lastActiveJobType = sessionStorage.getItem("lastActiveJobType");
      if (
        lastActiveJobType === "roadsAndSewers" ||
        lastActiveJobType === "groundworks"
      ) {
        return lastActiveJobType;
      }
    }
    // If only one job type is selected (from API or state), always use that one
    if (effectiveJobTypes.roadsAndSewers && !effectiveJobTypes.groundworks) {
      return "roadsAndSewers";
    }
    if (effectiveJobTypes.groundworks && !effectiveJobTypes.roadsAndSewers) {
      return "groundworks";
    }
    return state.activeJobType;
  };

  const handleTabChange = (key: string) => {
    // Save current data before switching tabs
    saveTender();

    setActiveJobType(key as "roadsAndSewers" | "groundworks");

    // Store the selected job type
    sessionStorage.setItem("lastActiveJobType", key);

    // Navigate to the first step of the selected workflow
    if (key === "roadsAndSewers") {
      navigate("/roads-sewers/preliminary");
    } else {
      navigate("/groundworks/cut-fills");
    }
  };

  const showSettingsModal = () => {
    setIsSettingsModalVisible(true);
  };

  const handleSettingsCancel = () => {
    setIsSettingsModalVisible(false);
  };

  const handleSettingsSave = async () => {
    setIsSettingsModalVisible(false);

    // GlobalSettingsCard already called saveTender() before invoking this
    // callback, but calling it again is harmless and ensures the persisted
    // payload is current (saveTender reads from its own stateRef internally).
    await saveTender();

    // Read from refs so we always use the post-update values, not the stale
    // closure values from the render in which this function was defined.
    const currentState = latestStateRef.current;
    const currentTotals = latestGroundworksTotalsRef.current;

    try {
      await forceSaveBothBoqEndpoints(
        currentState,
        "Global settings save",
        location.pathname.startsWith("/groundworks/") ? {
          grandTotal: currentTotals.grandTotal,
          grandTotalWithAdjustments: currentTotals.grandTotalWithAdjustments,
          preliminariesAmount: currentTotals.sections.reduce((s, sec) => s + sec.preliminariesAmount, 0),
          ohpAmount: currentTotals.sections.reduce((s, sec) => s + sec.ohpAmount, 0),
          discountAmount: currentTotals.discountAmount,
          tenderTotal: currentTotals.tenderTotal,
          preliminariesPercent: currentTotals.preliminariesPercent,
          ohpPercent: currentTotals.ohpPercent,
          discountPercent: currentTotals.discountPercent,
        } : undefined,
        user?.name,
        itemLibraryData,
        currentState.opportunityNo,
      );
    } catch (error) {
      console.error("Error saving to BOQ endpoints after global settings:", error);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#F1F3F9" }}>
      {/* Fixed Header */}
      <Header
        groundworksTenderTotal={groundworksTotals.tenderTotal}
        style={{
          position: "fixed",
          top: 0,
          left: showSidebar ? (sidebarCollapsed ? "64px" : "220px") : 0,
          right: 0,
          width: showSidebar
            ? `calc(100% - ${sidebarCollapsed ? "64px" : "220px"})`
            : "100%",
          zIndex: 1002,
          height: "88px",
          transition: "left 0.2s ease, width 0.2s ease",
        }}
      />

      {/* Global Settings Sidebar - Show floating button only when sidebar is hidden */}
      {isWorkflowStep && !showSidebar && (
        <div
          style={{
            position: "fixed",
            left: 0,
            bottom: "20px",
            zIndex: 100,
          }}
        >
          <div
            style={{
              margin: "20px",
              padding: "12px",
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: "#fff",
              border: "1px solid #f0f0f0",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "all 0.2s ease",
              opacity: canEditGlobalSettings ? 1 : 0.8,
            }}
            onClick={showSettingsModal}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
            }}
            title={
              canEditGlobalSettings
                ? "Global Settings"
                : `Global Settings (View Only - ${
                    !isBoqInProgress
                      ? "BOQ must be In-progress to edit"
                      : "tender is open in view mode"
                  })`
            }
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SettingOutlined
                style={{
                  fontSize: "16px",
                  color: "#8c8c8c",
                }}
              />
              <span
                style={{
                  fontWeight: "500",
                  fontSize: "14px",
                  color: "#262626",
                }}
              >
                Global Settings
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Sidebar - show when in workflow */}
      {showSidebar && (
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: sidebarCollapsed ? "64px" : "220px",
            height: "100vh",
            backgroundColor: "#0B2545",
            borderRight: "none",
            zIndex: 1001,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            transition: "width 0.2s ease",
          }}
        >
          {/* Logo and Toggle Section */}
          {!sidebarCollapsed && (
            <div
              style={{
                padding: "20px 16px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "block",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (!guardNavigation("/")) {
                      navigate("/");
                    }
                  }}
                >
                  <img
                    src={MJ_LOGO}
                    alt="MNJ Construction Logo"
                    style={{
                      height: "64px",
                      width: "auto",
                      maxWidth: "140px",
                      objectFit: "contain",
                      cursor: "pointer",
                    }}
                  />
                </div>
                <Button
                  type="text"
                  icon={<MenuFoldOutlined />}
                  onClick={toggleSidebar}
                  style={{
                    fontSize: "16px",
                    width: "32px",
                    height: "32px",
                    color: "#fff",
                  }}
                />
              </div>
            </div>
          )}
          {/* Toggle Button - only when collapsed */}
          {sidebarCollapsed && (
            <div
              style={{
                padding: "16px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                type="text"
                icon={<MenuUnfoldOutlined />}
                onClick={toggleSidebar}
                style={{
                  fontSize: "16px",
                  width: "32px",
                  height: "32px",
                  color: "#fff",
                }}
              />
            </div>
          )}
          {/* Back to Dashboard */}
          <div style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <div
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                backgroundColor: "transparent",
                transition: "all 0.2s ease",
                display: "flex",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
              }}
              onClick={() => {
                if (!guardNavigation("/")) {
                  navigate("/");
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              title={sidebarCollapsed ? "Back to Dashboard" : ""}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <ArrowLeftOutlined style={{ fontSize: "18px", color: "#fff" }} />
                {!sidebarCollapsed && (
                  <span style={{ fontWeight: "500", fontSize: "14px", color: "#fff" }}>
                    Back to Dashboard
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Job Type Switcher - only show when both job types selected */}
          {showTabs && (
            <div style={{ padding: "8px 0", flex: 1 }}>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div
                  style={{
                    padding: "12px 10px",
                    cursor: "pointer",
                    backgroundColor:
                      getCurrentTabKey() === "groundworks" &&
                      !isReviewRoute &&
                      !isDocumentLibraryWithTender &&
                      !isRateEnginePage
                        ? "#1890ff"
                        : "transparent",
                    transition: "all 0.2s ease",
                    display: "flex",
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                  }}
                  onClick={() => handleTabChange("groundworks")}
                  onMouseEnter={(e) => {
                    if (
                      getCurrentTabKey() !== "groundworks" ||
                      isReviewRoute ||
                      isDocumentLibraryWithTender ||
                      isRateEnginePage
                    ) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      getCurrentTabKey() !== "groundworks" ||
                      isReviewRoute ||
                      isDocumentLibraryWithTender ||
                      isRateEnginePage
                    ) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  title={sidebarCollapsed ? GROUNDWORKS_MENU_LABEL : ""}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Layers3 size={18} color="#fff" />
                    {!sidebarCollapsed && (
                      <div style={{ minWidth: 0, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            fontWeight: "500",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            color: "#fff",
                          }}
                        >
                          {GROUNDWORKS_MENU_LABEL}
                        </span>
                        {groundworksCurrentVersion !== undefined && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "rgba(255, 255, 255, 0.7)",
                              fontWeight: "400",
                            }}
                          >
                            (v{groundworksCurrentVersion})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px 10px",
                    cursor: "pointer",
                    backgroundColor:
                      getCurrentTabKey() === "roadsAndSewers" &&
                      !isReviewRoute &&
                      !isDocumentLibraryWithTender &&
                      !isRateEnginePage
                        ? "#1890ff"
                        : "transparent",
                    transition: "all 0.2s ease",
                    display: "flex",
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                  }}
                  onClick={() => handleTabChange("roadsAndSewers")}
                  onMouseEnter={(e) => {
                    if (
                      getCurrentTabKey() !== "roadsAndSewers" ||
                      isReviewRoute ||
                      isDocumentLibraryWithTender ||
                      isRateEnginePage
                    ) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      getCurrentTabKey() !== "roadsAndSewers" ||
                      isReviewRoute ||
                      isDocumentLibraryWithTender ||
                      isRateEnginePage
                    ) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  title={sidebarCollapsed ? ROADS_SEWERS_MENU_LABEL : ""}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Route size={18} color="#fff" />
                    {!sidebarCollapsed && (
                      <div style={{ minWidth: 0, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            fontWeight: "500",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            color: "#fff",
                          }}
                        >
                          {ROADS_SEWERS_MENU_LABEL}
                        </span>
                        {roadsAndSewersCurrentVersion !== undefined && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "rgba(255, 255, 255, 0.7)",
                              fontWeight: "400",
                            }}
                          >
                            (v{roadsAndSewersCurrentVersion})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Workflow Title - show when only one job type selected */}
          {!showTabs && (isWorkflowStep || isDocumentLibraryWithTender || isRateEnginePage) && (
            <div style={{ padding: "8px 0" }}>
              <div
                style={{
                  padding: "12px 10px",
                  backgroundColor:
                    isReviewRoute || isDocumentLibraryWithTender || isRateEnginePage
                      ? "transparent"
                      : "#1890ff",
                  display: "flex",
                  justifyContent: sidebarCollapsed ? "center" : "flex-start",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onClick={() => {
                  const tabKey = getCurrentTabKey();
                  // Store the job type before navigating
                  sessionStorage.setItem("lastActiveJobType", tabKey);
                  if (tabKey === "roadsAndSewers") {
                    navigate("/roads-sewers/preliminary");
                  } else if (tabKey === "groundworks") {
                    navigate("/groundworks/cut-fills");
                  }
                }}
                onMouseEnter={(e) => {
                  if (isReviewRoute || isDocumentLibraryWithTender || isRateEnginePage) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isReviewRoute || isDocumentLibraryWithTender || isRateEnginePage) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
                title={
                  sidebarCollapsed
                    ? getCurrentTabKey() === "roadsAndSewers"
                      ? ROADS_SEWERS_MENU_LABEL
                      : GROUNDWORKS_MENU_LABEL
                    : ""
                }
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {getCurrentTabKey() === "roadsAndSewers" ? (
                    <Route size={18} color="#fff" />
                  ) : (
                    <Layers3 size={18} color="#fff" />
                  )}
                  {!sidebarCollapsed && (
                    <div
                      style={{
                        flexDirection: "column",
                        gap: "2px",
                        minWidth: 0,
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "500",
                          fontSize: "12px",
                            whiteSpace: "nowrap",
                          color: "#fff",
                        }}
                      >
                        {getCurrentTabKey() === "roadsAndSewers"
                          ? ROADS_SEWERS_MENU_LABEL
                          : GROUNDWORKS_MENU_LABEL}
                      </span>
                      {(() => {
                        const currentTabVersion =
                          getCurrentTabKey() === "roadsAndSewers"
                            ? roadsAndSewersCurrentVersion
                            : groundworksCurrentVersion;
                        return currentTabVersion !== undefined && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "rgba(255, 255, 255, 0.7)",
                              fontWeight: "400",
                            }}
                          >
                            (v{currentTabVersion})
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div style={{ flex: 1 }} />
          {/* Navigation Items */}
          <div
            style={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                backgroundColor: isDocumentLibraryPage
                  ? "#1890ff"
                  : "transparent",
                transition: "all 0.2s ease",
                display: "flex",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
              }}
              onClick={() => {
                // Store current job type before navigating
                const currentJobType = getCurrentTabKey();
                sessionStorage.setItem("lastActiveJobType", currentJobType);
                if (!guardNavigation("/document-library")) {
                  navigate("/document-library");
                }
              }}
              onMouseEnter={(e) => {
                if (!isDocumentLibraryPage) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isDocumentLibraryPage) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
              title={sidebarCollapsed ? "Document Library" : ""}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <FileTextOutlined
                  style={{
                    fontSize: "18px",
                    color: "#fff",
                  }}
                />
                {!sidebarCollapsed && (
                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "14px",
                      color: "#fff",
                    }}
                  >
                    Document Library
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Site Rate Library */}
           <div
            style={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                backgroundColor: isRateEnginePage ? "#1890ff" : "transparent",
                transition: "all 0.2s ease",
                display: "flex",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
              }}
              onClick={() =>
                navigate(
                  (rateEngineLastRouteKey &&
                    localStorage.getItem(rateEngineLastRouteKey)) ||
                    "/rate-engine/activity-tree"
                )
              }
              onMouseEnter={(e) => {
                if (!isRateEnginePage) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isRateEnginePage) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
              title={sidebarCollapsed ? "Site Rate Library" : ""}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <DollarOutlined
                  style={{
                    fontSize: "18px",
                    color: "#fff",
                  }}
                />
                {!sidebarCollapsed && (
                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "14px",
                      color: "#fff",
                    }}
                  >
                    Site Rate Library
                  </span>
                )}
              </div>
            </div>
          </div> 
          {/* Global Settings at bottom of sidebar */}
          <div
            style={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                backgroundColor: "transparent",
                transition: "all 0.2s ease",
                display: "flex",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
                opacity: canEditGlobalSettings ? 1 : 0.75,
              }}
              onClick={showSettingsModal}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              title={
                canEditGlobalSettings
                  ? "Global Settings"
                  : `Global Settings (View Only - ${
                      !isBoqInProgress
                        ? "BOQ must be In-progress to edit"
                        : "tender is open in view mode"
                    })`
              }
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <SettingOutlined
                  style={{
                    fontSize: "18px",
                    color: "#fff",
                  }}
                />
                {!sidebarCollapsed && (
                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "14px",
                      color: "#fff",
                    }}
                  >
                    Global Settings
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div
        style={{
          marginTop: "88px",
          marginLeft: showSidebar ? (sidebarCollapsed ? "64px" : "220px") : "0",
          minHeight: "calc(100vh - 88px)",
          transition: "margin-left 0.2s ease",
        }}
      >
        {/* Stepper Navigation renders in WorkflowContainer so it persists across route changes */}

        {/* Page Content */}
        <div
          style={{
            marginTop: isWorkflowStep && showStepper ? "140px" : "0", // Space for fixed stepper
            paddingTop: "10px",
            paddingLeft: "24px",
            paddingRight: "24px",
            minHeight:
              isWorkflowStep && showStepper
                ? "calc(100vh - 228px)"
                : "calc(100vh - 112px)",
          }}
        >
          {/* View Mode Banner */}
          {/* {isViewMode && (
            <Alert
              message={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>
                    <strong>View Mode</strong> - You are viewing a historical version. All inputs are disabled and changes cannot be saved.
                  </span>
                  <Button
                    type="primary"
                    size="small"
                    className="view-mode-exit-btn"
                    onClick={() => {
                      setViewMode(false);
                      navigate("/opportunities");
                    }}
                    style={{ marginLeft: "16px" }}
                  >
                    Exit View Mode
                  </Button>
                </div>
              }
              type="warning"
              showIcon
              style={{
                marginBottom: "16px",
                position: "sticky",
                top: isWorkflowStep && showStepper ? "140px" : "0",
                zIndex: 100,
              }}
            />
          )} */}
          <div
            className={
              isViewMode &&
              !location.pathname.includes("/review") &&
              !location.pathname.includes("/bill-quantities") &&
              !location.pathname.includes("/document-library") &&
              !location.pathname.includes("/rate-engine")
                ? "view-mode-container"
                : ""
            }
            style={{ width: "100%" }}
          >
            {location.pathname === "/tender-info" ||
            location.pathname === "/new-tender" ||
            location.pathname === "/job-types" ||
            location.pathname === "/job-selection" ||
            location.pathname === "/document-library" ||
            location.pathname.startsWith("/groundworks/") ||
            location.pathname.startsWith("/roads-sewers/") ? (
              // No card wrapper for tender-info, new-tender, job-types, job-selection, document-library, groundworks, and roads-sewers pages
              children
            ) : (
              <Card
                style={{
                  minHeight: "60vh",
                  backgroundColor: isRateEnginePage
                    ? (state.projectName ? "#f0fdf4" : "#eff6ff")
                    : "#fff",
                  borderRadius: "8px",
                  boxShadow:
                    "0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)",
                }}
              >
                {children}
              </Card>
            )}
          </div>
        </div>
      </div>

      {showFooter && !isViewMode && (
        <Footer
          style={{
            backgroundColor: "#fff",
            borderTop: "1px solid #e5e7eb",
            padding: "16px 24px",
            boxShadow: "0 -1px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-end items-center gap-3">
              {onSaveAndExit && (
                <Button
                  onClick={handleSaveAndExit}
                  disabled={!canProceed}
                  style={{
                    borderColor: COLORS.primary,
                    color: COLORS.primary,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Save and Exit
                </Button>
              )}

              {((isWorkflowStep && !isLastWorkflowStep) ||
                location.pathname === "/tender-info" ||
                location.pathname === "/job-types" ||
                location.pathname === "/job-selection") && (
                <Button
                  type="primary"
                  onClick={handleNext}
                  disabled={!canProceed}
                  style={{
                    backgroundColor: !canProceed ? undefined : COLORS.primary,
                    borderColor: !canProceed ? undefined : COLORS.primary,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Save & Continue <ArrowRightOutlined />
                </Button>
              )}

              {isLastWorkflowStep && shouldShowBillQuantities() && (
                <Button
                  type="primary"
                  onClick={handleNext}
                  disabled={!canProceed}
                  style={{
                    backgroundColor: !canProceed ? undefined : COLORS.primary,
                    borderColor: !canProceed ? undefined : COLORS.primary,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Go to Bill of Quantities <ArrowRightOutlined />
                </Button>
              )}

              {isFinalStep && (
                <Button
                  type="primary"
                  onClick={handleSave}
                  style={{
                    backgroundColor: "#10b981",
                    borderColor: "#10b981",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Complete Tender <ArrowRightOutlined />
                </Button>
              )}
            </div>
          </div>
        </Footer>
      )}

      {/* Global Settings Modal */}
      <Modal
        title={`Global Settings - ${
          getCurrentTabKey() === "roadsAndSewers"
            ? "Roads & Sewers"
            : "Groundworks"
        }${canEditGlobalSettings ? "" : " (View Only)"}`}
        open={isSettingsModalVisible}
        onCancel={handleSettingsCancel}
        footer={null}
        width={680}
        centered
        closable={true}
        destroyOnClose={true}
        className="global-settings-modal"
        styles={{
          header: {
            backgroundColor: "#f5f5f5",
            marginLeft: "-24px",
            marginRight: "-24px",
            marginTop: "-20px",
            paddingLeft: "24px",
            paddingRight: "24px",
            paddingTop: "16px",
            paddingBottom: "16px",
          },
        }}
      >
        {isSettingsModalVisible && (
          <GlobalSettingsCard
            inModal={true}
            isViewMode={!canEditGlobalSettings}
            onSave={handleSettingsSave}
            onCancel={handleSettingsCancel}
            selectedJobTypes={(() => {
              const currentJobType = getCurrentTabKey();
              return {
                roadsAndSewers: currentJobType === "roadsAndSewers",
                groundworks: currentJobType === "groundworks",
              };
            })()}
          />
        )}
      </Modal>

      {/* Review Selection Modal - Choose between Groundworks and Roads & Sewers Review */}
      <Modal
        title="Select Review Page"
        open={isReviewSelectionModalVisible}
        onCancel={() => setIsReviewSelectionModalVisible(false)}
        footer={null}
        width={500}
        closable={true}
        centered
        styles={{
          header: {
            backgroundColor: "#f5f5f5",
            marginLeft: "-24px",
            marginRight: "-24px",
            marginTop: "-20px",
            paddingLeft: "24px",
            paddingRight: "24px",
            paddingTop: "16px",
            paddingBottom: "16px",
          },
        }}
      >
        <div className="py-4">
          <p className="text-base mb-4 text-gray-700">
            Please select which review page you want to navigate to:
          </p>
          <div className="flex flex-col gap-3">
            <Button
              type="primary"
              icon={<Layers3 size={16} />}
              size="large"
              onClick={() => {
                navigate("/groundworks/review");
                setIsReviewSelectionModalVisible(false);
              }}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
                height: "48px",
                fontSize: "16px",
              }}
            >
              Groundworks Review
            </Button>
            <Button
              type="primary"
              icon={<Route size={16} />}
              size="large"
              onClick={() => {
                navigate("/roads-sewers/review");
                setIsReviewSelectionModalVisible(false);
              }}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
                height: "48px",
                fontSize: "16px",
              }}
            >
              Roads & Sewers Review
            </Button>
          </div>
        </div>
      </Modal>

      {/* Session Idle Modal */}
      <SessionIdleModal
        open={isIdleModalOpen}
        saving={isIdleSaving}
        onCancel={handleIdleCancel}
        onSave={handleIdleSave}
      />

      {/* Unsaved Changes Navigation Modal */}
      <UnsavedChangesModal
        open={navBlockModalOpen}
        saving={isNavBlockSaving}
        onCancel={handleNavBlockCancel}
        onDiscardAndContinue={handleNavBlockDiscardAndContinue}
        onSave={handleNavBlockSave}
      />
    </div>
  );
};

export default MainLayout;
